import { Suspense, lazy, useState } from "react";
import { Maximize2 } from "lucide-react";
import { normalizePostDoc, postDocFromText, postDocHasContent, postWordCount, type PostDoc } from "@baze/schema";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { t } from "@/i18n/id";

// Tiptap is only downloaded when a lesson topic is opened for writing.
const RichTextEditor = lazy(() => import("@/features/blog/RichTextEditor").then((m) => ({ default: m.RichTextEditor })));

interface Topic {
  title: string;
  body?: string;
  doc?: PostDoc;
}

/**
 * "Isi materi": rich text (headings, bold, lists, links, quotes, images) for one lesson topic, in the
 * inspector or full screen for long writing. An old plain-text body opens as paragraphs; the first edit
 * stores it as rich content and drops the plain copy.
 */
export function LessonContentField({ siteId, topic, onChange }: { siteId: string; topic: Topic; onChange: (patch: { doc?: PostDoc; body?: string }) => void }) {
  const [full, setFull] = useState(false);
  // Remounts the inline editor after full-screen writing (Tiptap reads its value only on mount).
  const [rev, setRev] = useState(0);
  const doc = topic.doc ?? postDocFromText(topic.body);
  const words = postWordCount(doc);
  const commit = (json: PostDoc) => {
    const next = normalizePostDoc(json);
    onChange(postDocHasContent(next) ? { doc: next, body: undefined } : { doc: undefined, body: undefined });
  };
  const stats = words ? t.editor.lessonContent.stats(words, Math.max(1, Math.round(words / 200))) : t.editor.lessonContent.empty;

  return (
    <div className="space-y-1.5">
      <div className="flex items-end justify-between gap-3">
        <p className="text-sm font-semibold text-slate-800">{t.editor.fields.lessonBody}</p>
        <Button variant="ghost" size="sm" className="-mr-2 h-8" onClick={() => setFull(true)}>
          <Maximize2 aria-hidden="true" />
          {t.editor.lessonContent.fullscreen}
        </Button>
      </div>
      {full ? (
        <button type="button" onClick={() => setFull(true)} className="flex h-24 w-full items-center justify-center rounded-2xl border border-dashed border-slate-300 text-sm text-slate-500">
          {t.editor.lessonContent.editingFull}
        </button>
      ) : (
        <Suspense fallback={<Skeleton className="h-52 rounded-2xl" />}>
          <RichTextEditor key={rev} siteId={siteId} value={doc} onChange={commit} size="compact" placeholder={t.editor.lessonContent.placeholder} />
        </Suspense>
      )}
      <p className="text-xs text-slate-500">
        {stats} · {t.editor.lessonContent.shortcuts}
      </p>

      <Dialog
        open={full}
        onOpenChange={(o) => {
          setFull(o);
          if (!o) setRev((r) => r + 1);
        }}
      >
        <DialogContent size="workspace" title={topic.title} description={t.editor.lessonContent.fullDescription} scroll={false}>
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto bg-slate-50 px-3 pb-6 sm:px-8">
            <div className="mx-auto max-w-3xl pt-2">
              <Suspense fallback={<Skeleton className="h-[60vh] rounded-2xl" />}>
                <RichTextEditor siteId={siteId} value={doc} onChange={commit} placeholder={t.editor.lessonContent.placeholder} autoFocus />
              </Suspense>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-line px-5 py-3 sm:px-6">
            <p className="text-xs text-slate-500">{stats}</p>
            <Button
              size="sm"
              onClick={() => {
                setFull(false);
                setRev((r) => r + 1);
              }}
            >
              {t.editor.lessonContent.done}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
